import {chain} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {EmojiCategoriesEnum} from '../../../../services/emojis/emojiList';
import {categoryRenderers} from './emojiDropdownCommon';

/*
 * Props.
 */

export interface EmojiDropdownCategoriesProps {
  showRecentCategory?: boolean;
  activeCategory?: EmojiCategoriesEnum;
  onCategorySelect: HandlerOf<EmojiCategoriesEnum>;
  showCustomEmojiCategory: boolean;
}

/*
 * Style.
 */

const StyledCategoriesDiv = styled.div`
  flex: 1;
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  margin: 0 8px -1px 8px;
`;

const StyledCategoryDiv = styled(ChromelessButton)`
  border-bottom: 2px solid transparent;
  ${center()};
  ${(p) => colorStyle(Boolean(p.isActive))};
`;

function colorStyle(isActive: boolean) {
  // If this is the active category, blue + no hover.
  if (isActive) {
    return css`
      color: ${(p) => p.theme.interactiveText};
      border-color: ${(p) => p.theme.palette.blue.shade40};
    `;
  }

  // Otherwise, grey + hover.
  return css`
    color: ${(p) => p.theme.greys.shade40};
    &:hover {
      color: ${(p) => p.theme.greys.shade60};
      border-color: ${(p) => p.theme.greys.shade60};
    }
  `;
}

/*
 * Component.
 */

export const EmojiDropdownCategories: FC<EmojiDropdownCategoriesProps> = (props) => {
  // Render each category button.
  const categoryRenders = chain(EmojiCategoriesEnum)
    .filter((c) => Number.isInteger(Number(c)))
    .filter((c) => Boolean(c !== EmojiCategoriesEnum.FREQUENTLY_USED || props.showRecentCategory))
    .filter((c) => Boolean(c !== EmojiCategoriesEnum.CUSTOM || props.showCustomEmojiCategory))
    .map((c) => renderCategory(props, c))
    .value();

  // Wrap and return.
  return <StyledCategoriesDiv>{categoryRenders}</StyledCategoriesDiv>;
};

function renderCategory(props: EmojiDropdownCategoriesProps, category: EmojiCategoriesEnum) {
  const {activeCategory, onCategorySelect} = props;
  return (
    <StyledCategoryDiv
      key={category}
      isActive={category === activeCategory}
      onClick={() => onCategorySelect(category)}
    >
      <Icon name={categoryShapeNames[category]} />
      <VisuallyHidden>{categoryRenderers[category]}</VisuallyHidden>
    </StyledCategoryDiv>
  );
}

type CategoryShapeNames = {[T in EmojiCategoriesEnum]: IconName};
const categoryShapeNames: CategoryShapeNames = {
  [EmojiCategoriesEnum.FREQUENTLY_USED]: 'snooze',
  [EmojiCategoriesEnum.SMILEYS_AND_PEOPLE]: 'emojiPeople',
  [EmojiCategoriesEnum.ANIMALS_AND_NATURE]: 'emojiNature',
  [EmojiCategoriesEnum.FOOD_AND_DRINK]: 'emojiFood',
  [EmojiCategoriesEnum.ACTIVITY]: 'emojiActivity',
  [EmojiCategoriesEnum.TRAVEL_AND_PLACES]: 'emojiTravel',
  [EmojiCategoriesEnum.OBJECTS]: 'emojiObjects',
  [EmojiCategoriesEnum.SYMBOLS]: 'emojiSymbols',
  [EmojiCategoriesEnum.FLAGS]: 'emojiFlags',
  [EmojiCategoriesEnum.CUSTOM]: 'star',
};
